import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import SearchIcon from "@material-ui/icons/Search";
import { CSVLink } from "react-csv";
import { format } from "date-fns";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useTimezone } from "../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  panel: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3)
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  searchField: {
    flex: 1,
    minWidth: 260
  },
  statusFilter: {
    minWidth: 180
  },
  tableContainer: {
    overflowX: "auto"
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4)
  }
}));

const chipColor = (status) => ({
  sent: "#4caf50",
  failed: "#f44336",
  pending: "#607d8b",
  processing: "#ff9800",
  cancelled: "#757575"
}[status] || "#757575");

const statusLabel = (status) => ({
  sent: "Enviado",
  failed: "Fallido",
  pending: "Pendiente",
  processing: "Procesando",
  cancelled: "Cancelado"
}[status] || status || "N/A");

const ShippingDetailTable = ({ startDate, endDate, selectedCampaignId }) => {
  const classes = useStyles();
  const csvLinkRef = useRef(null);
  const { formatDateTime } = useTimezone();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadRecords = async () => {
      setLoading(true);
      try {
        const params = {
          page: page + 1,
          limit: rowsPerPage,
          status: filterStatus,
          search: searchTerm,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        };
        if (selectedCampaignId) params.campaignId = selectedCampaignId;

        const { data } = await api.get("/group-campaign-shipping-reports/detail", {
          params
        });
        if (mounted) {
          setRecords(data.records || []);
          setTotal(data.total || 0);
        }
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRecords();
    return () => {
      mounted = false;
    };
  }, [endDate, filterStatus, page, rowsPerPage, searchTerm, selectedCampaignId, startDate]);

  const exportCsv = () => {
    setCsvData(records.map((record) => ({
      Campaña: record.campaign?.name || "N/A",
      Grupo: record.groupDetails?.name || "N/A",
      "ID Grupo": record.groupId || "N/A",
      Ejecucion: record.executionNumber || "N/A",
      Estado: statusLabel(record.status),
      "Fecha envio": record.sentAt ? formatDateTime(record.sentAt) : "N/A",
      Mensaje: record.message || "N/A",
      Error: record.error || ""
    })));
    setTimeout(() => csvLinkRef.current?.link.click(), 100);
  };

  return (
    <Paper className={classes.panel}>
      <Typography variant="h6" className={classes.title}>Detalle de Envios</Typography>
      <Box className={classes.filtersRow}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          placeholder="Buscar por nombre o ID de grupo..."
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        <TextField
          select
          className={classes.statusFilter}
          variant="outlined"
          size="small"
          label="Estado"
          value={filterStatus}
          onChange={(event) => {
            setFilterStatus(event.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="sent">Enviados</MenuItem>
          <MenuItem value="failed">Fallidos</MenuItem>
          <MenuItem value="pending">Pendientes</MenuItem>
          <MenuItem value="cancelled">Cancelados</MenuItem>
        </TextField>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={exportCsv}
          disabled={records.length === 0}
        >
          Exportar CSV
        </Button>
        <CSVLink
          data={csvData}
          filename={`informe-envios-grupos-${format(new Date(), "dd-MM-yyyy")}.csv`}
          ref={csvLinkRef}
          style={{ display: "none" }}
        />
      </Box>
      {loading ? (
        <Box className={classes.loading}><CircularProgress /></Box>
      ) : (
        <>
          <Box className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaña</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell>ID Grupo</TableCell>
                  <TableCell align="center">Ejecucion</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell>Fecha de Envio</TableCell>
                  <TableCell>Mensaje</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No hay registros para mostrar
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.campaign?.name || "N/A"}</TableCell>
                    <TableCell>{record.groupDetails?.name || "N/A"}</TableCell>
                    <TableCell>{record.groupId || "N/A"}</TableCell>
                    <TableCell align="center">{record.executionNumber || "N/A"}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={statusLabel(record.status)}
                        style={{ backgroundColor: chipColor(record.status), color: "white" }}
                      />
                    </TableCell>
                    <TableCell>{record.sentAt ? formatDateTime(record.sentAt) : "N/A"}</TableCell>
                    <TableCell>
                      {record.message?.length > 55
                        ? `${record.message.substring(0, 55)}...`
                        : record.message || "N/A"}
                    </TableCell>
                    <TableCell>
                      {record.error?.length > 70
                        ? `${record.error.substring(0, 70)}...`
                        : record.error || ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por pagina:"
          />
        </>
      )}
    </Paper>
  );
};

export default ShippingDetailTable;
